// Where the merchant's hosted checkout sends the player back (web wave 8): money's `CHECKOUT_RETURN_URL` is
// `<site>/checkout/done` with `?order=<order_id>` appended (payments, wave 8). /checkout/success, /checkout/cancelled
// and /checkout/pending take the same `?order=` for merchants that tell success, cancel and pending apart. Coins are bought in the launcher, never here (docs/PLAN.md: the
// website is browse only), so these pages only say what happened and offer "Back to Threedom".
//
// Trust: the URL is a hint. The page reads nothing from it but a well-formed order id. Amounts, currency and status
// in the query (the merchant may append its own) are ignored. With a session, the order's status comes from money's
// `GET /v1/me/orders` (only the buyer's own orders are listed, so someone else's id finds nothing); otherwise the page
// shows a message that promises nothing. No payment details are ever shown: not the price, currency, country, card or
// checkout id. At most the coin count from money's own record.

import { COIN_SYMBOL } from "./money.ts";

export type ReturnHint = "success" | "cancelled" | "pending" | "unknown";

export const RETURN_HINTS: readonly ReturnHint[] = ["success", "cancelled", "pending"];
/** `done` is money's one return URL: the merchant sends buyers there after paying, so it reads like success. */
const HINT_ALIASES: Record<string, ReturnHint> = { done: "success" };

/** Money's order ids: `o_` + a ULID. */
export const ORDER_ID = /^o_[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * "Back to Threedom": the launcher's `thrdm://checkout/return?order=o_…` (release, wave 8 `links.rs`). It only tells a
 * top-up waiting for that order to ask money again. The launcher rejects it without a valid order, so no order, no link.
 */
export function returnLink(orderId: string | null): string | null {
  return orderId && ORDER_ID.test(orderId) ? `thrdm://checkout/return?order=${orderId}` : null;
}

export function parseReturn(state: string | undefined, query: URLSearchParams): { hint: ReturnHint; orderId: string | null } {
  const hint = (RETURN_HINTS as readonly string[]).includes(state ?? "") ? (state as ReturnHint) : (HINT_ALIASES[state ?? ""] ?? "unknown");
  const raw = query.get("order");
  return { hint, orderId: raw && ORDER_ID.test(raw) ? raw : null };
}

export type OrderState = "pending" | "expired" | "paid" | "refunded";

/** The only fields the page keeps from money's order: never `paid`, `display`, `gross_cents` or dates. */
export interface OrderStatus {
  orderId: string;
  status: OrderState;
  coins: number;
}

/** Find this order in `GET /v1/me/orders`. Anything malformed counts as not found. */
export function pickOrder(raw: unknown, orderId: string): OrderStatus | null {
  const orders = raw && typeof raw === "object" && Array.isArray((raw as { orders?: unknown }).orders) ? ((raw as { orders: unknown[] }).orders) : [];
  for (const o of orders) {
    if (!o || typeof o !== "object") continue;
    const r = o as Record<string, unknown>;
    if (r.order_id !== orderId) continue;
    const status = r.status;
    if (status !== "pending" && status !== "expired" && status !== "paid" && status !== "refunded") return null;
    const coins = typeof r.coins === "number" && Number.isSafeInteger(r.coins) && r.coins > 0 ? r.coins : 0;
    return { orderId, status, coins };
  }
  return null;
}

export type ViewKind = "arrived" | "on_the_way" | "not_charged" | "expired" | "refunded" | "generic";

export interface ReturnView {
  kind: ViewKind;
  heading: string;
  body: string;
  tone: "good" | "neutral";
  /** keep asking money for a little while (a pending order after a success return) */
  poll: boolean;
}

const coinsText = (n: number) => `${n.toLocaleString("en-US")} ${COIN_SYMBOL}`;

/**
 * What the page says. Money's status beats the URL: a paid order reads as paid even on /cancelled, a pending one on
 * /cancelled reads as not charged. Without an order (signed out, someone else's id, money unreachable) the text
 * follows the URL but promises nothing.
 */
export function returnView(hint: ReturnHint, order: OrderStatus | null): ReturnView {
  if (order) {
    switch (order.status) {
      case "paid":
        return {
          kind: "arrived",
          heading: "Your coins have arrived",
          body: order.coins ? `${coinsText(order.coins)} are in your Banger wallet.` : "They're in your Banger wallet.",
          tone: "good",
          poll: false,
        };
      case "refunded":
        return { kind: "refunded", heading: "This top-up was refunded", body: "Its coins left your wallet when it was refunded. Banger shows every top-up.", tone: "neutral", poll: false };
      case "expired":
        // a merchant can still take a late payment; money then credits it and the order turns paid
        if (hint === "cancelled")
          return { kind: "expired", heading: "Nothing was charged", body: "This checkout expired before it was paid. You can top up again in Banger.", tone: "neutral", poll: false };
        return {
          kind: "expired",
          heading: "This checkout had expired",
          body: "If you were charged anyway, your coins still reach your Banger wallet. If not, nothing was charged and you can top up again in Banger.",
          tone: "neutral",
          poll: true,
        };
      case "pending":
        if (hint === "cancelled")
          return { kind: "not_charged", heading: "Nothing was charged", body: "You left the checkout before paying. You can top up again in Banger whenever you like.", tone: "neutral", poll: false };
        return {
          kind: "on_the_way",
          heading: "Your coins are on the way",
          body: "We're waiting for the payment to be confirmed. Your coins show up in Banger as soon as it is, and you can close this page.",
          tone: "good",
          poll: true,
        };
    }
  }
  if (hint === "success")
    return { kind: "on_the_way", heading: "Your coins are on the way", body: "If your payment went through, your coins will show in Banger shortly. Banger lists every top-up.", tone: "good", poll: false };
  if (hint === "pending")
    return {
      kind: "on_the_way",
      heading: "Your payment is being confirmed",
      body: "Some ways to pay take a little while. Your coins will show in Banger once the payment is confirmed.",
      tone: "neutral",
      poll: false,
    };
  if (hint === "cancelled")
    return { kind: "not_charged", heading: "Nothing was charged", body: "If you left the checkout without paying, nothing was charged. You can top up again in Banger.", tone: "neutral", poll: false };
  return { kind: "generic", heading: "Back from checkout", body: "Banger shows your balance and every top-up.", tone: "neutral", poll: false };
}

/** Milliseconds between status checks while an order is pending: about 30 s in all, then the page stops asking. */
export const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 12000];
